import React, { useCallback, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getCIPs } from "../../network/cipAPI";
import AgTable from "../../components/AgTable";
import { useState } from "react";
import MetricSummaryCard from "../../components/MetricSummaryCard";

import { storeCIPS, storeMetricData } from "../../Store/TemplateStore/template.action";
import errorIcon from "../../assets/error-icon.svg";
import PageLoading from "../../components/PageLoading";
import { HISTORIC_CIP_TABLE_COLUMNS } from "./historicCIPTable.config";
import { useTranslation } from "react-i18next";
import { getCleaningMetricsCardsConfig } from "../cleaningMetricsCards.config";
import "./index.scss";

const HistoricalCIPs = ({}) => {
  const { t } = useTranslation();
  const temperature = useSelector((state) => state.temperature);
  const mass = useSelector((state) => state.mass);
  const pressure = useSelector((state) => state.pressure);
  const [rowData, setRowData] = useState([]);
  const gridRef = useRef(null);
  const [gridApi, setGridAPi] = useState(null);
  const [isFetchingCIPs, setFetchingCIP] = useState(false);
  const [isDataLoaded, setIsDataLoaded] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [allCipData, setAllCipData] = useState({
    cleanings: [],
    backwashes: [],
  });
  const [metricData, setMetricData] = useState(null);
  const colDefs = HISTORIC_CIP_TABLE_COLUMNS(t, temperature, mass);

  const dispatch = useDispatch();

  const onGridReady = useCallback((params) => {
    let gridApi = params.api;
    setGridAPi(gridApi);
  });

  const updateStoreCips = (data) => {
    dispatch(storeCIPS([...data]));
  };

  const updateStoreBackwashes = (data) => {
    dispatch({ type: "STORE_BACKWASHES", payload: [...data] });
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        setFetchingCIP(true);
        setIsLoading(true);
        const data = await getCIPs();
        setFetchingCIP(false);
        if (data && data.status === 200 && data?.data && data?.data.cleanings) {
          // Extract cleanings, backwashes, and metric data
          const { cleanings, backwashes, ...metricsData } = data.data;
          setAllCipData({ cleanings, backwashes });
          setRowData(cleanings); // Only show cleanings in the table
          setMetricData(metricsData); // Store all metric data from API
          setIsLoading(false);
          updateStoreCips(cleanings);
          updateStoreBackwashes(backwashes);
          // Store metric data in Redux for use on other pages
          dispatch(storeMetricData(metricsData));
        } else {
          setRowData([]);
          setAllCipData({ cleanings: [], backwashes: [] });
          setMetricData(null);
          updateStoreCips([]);
          dispatch(storeMetricData(null));
        }
      } catch (err) {
        setFetchingCIP(false);
      } finally {
        setIsDataLoaded(true);
        setIsLoading(false);
      }
    }
    if (!isFetchingCIPs) {
      fetchData();
    }
  }, []);

  // Prepare export data: cleanings, gap, backwashes
  const getExportData = () => {
    const { cleanings, backwashes } = allCipData;
    if (!cleanings.length && !backwashes.length) return [];
    // Add 5 empty rows as gap
    const emptyRows = [{}, {}, {}, {}, {}];
    // Add exportIndex to cleanings
    const cleaningsWithIndex = cleanings.map((c, i) => ({
      ...c,
      exportIndex: i + 1,
    }));
    // Add exportIndex to backwashes, starting after cleanings
    const backwashesWithIndex = backwashes.map((bw, i) => ({
      ...bw,
      exportIndex: cleanings.length + i + 1,
    }));
    return [...cleaningsWithIndex, ...emptyRows, ...backwashesWithIndex];
  };

  const metricCardsConfig = metricData
    ? getCleaningMetricsCardsConfig(metricData, t)
    : [];

  return (
    <div className="historic-container">
      {rowData && rowData.length > 0 && (
        <>
          <div className="operational-metrics">
            {metricCardsConfig.map((card) => (
              <MetricSummaryCard
                key={card.key}
                title={card.title}
                rawValue={card.rawValue}
                value={card.value}
                targetLabel={card.targetLabel}
                targetValue={card.targetValue}
                rangeLabel={card.rangeLabel}
                rangeValue={card.rangeValue}
                infoDescription={card.infoDescription}
                icon={card.icon}
              />
            ))}
          </div>
          {isLoading ? (
            <PageLoading customHeight={"215px"} />
          ) : (
            <AgTable
              gridRef={gridRef}
              rowData={rowData}
              columnDefs={colDefs}
              onGridReady={onGridReady}
              name={`${t("excelSheetNames.historicalCIPS")}.csv`}
              infoPopoverText={t(
                "IdentifiedCIPStrings.cleaningEffectivenessSummary"
              )}
              questionPopoverText={t(
                "IdentifiedCIPStrings.troubleshootingCIPsAndCEBs"
              ).replaceAll("<unit>", pressure || "bar/psig")}
              exportData={getExportData()}
            />
          )}
        </>
      )}
      {rowData && rowData.length === 0 && isDataLoaded && (
        <div className="errorText12 card-error">
          <img src={errorIcon}></img>
          {t("IdentifiedCIPStrings.noCip")}
        </div>
      )}
    </div>
  );
};

export default HistoricalCIPs;
